import type { FocusEvent, ReactNode } from 'react'
import { useFieldContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import { Input, type InputProps } from '../ui/input'
import { Label } from '../ui/label'
import FieldInfo from './FieldInfo'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'

type SocialPlatform = {
  prefix: string
  placeholder: string
}

export type SocialUrlInputProps = InputProps & {
  label?: ReactNode
  labelEnd?: ReactNode
  readOnlyEmptyText?: ReactNode
}

export default function UrlInput({
  id,
  label,
  type,
  labelEnd,
  className,
  readOnlyEmptyText = 'No URL provided',
  ...props
}: SocialUrlInputProps) {
  const field = useFieldContext<string | null | undefined>()
  const { isReadOnly } = useIsReadOnly()

  const parsedName = field.name?.split('.')
  // split() always returns at least one segment, so the fallback never applies.
  const fieldName = parsedName[parsedName.length - 1] ?? ''

  const platforms: Partial<Record<string, SocialPlatform>> = {
    youtube_link: {
      prefix: 'https://www.youtube.com/',
      placeholder: 'https://www.youtube.com/your-channel',
    },
    instagram_link: {
      prefix: 'https://www.instagram.com/',
      placeholder: 'https://www.instagram.com/your-username',
    },
    facebook_link: {
      prefix: 'https://www.facebook.com/',
      placeholder: 'https://www.facebook.com/your-username',
    },
    pinterest_link: {
      prefix: 'https://www.pinterest.com/',
      placeholder: 'https://www.pinterest.com/your-username',
    },
    tiktok_link: {
      prefix: 'https://www.tiktok.com/',
      placeholder: 'https://www.tiktok.com/@your-username',
    },
  }
  const platform = platforms[fieldName]

  const extractHandle = (inputValue: string) => {
    if (!inputValue) return ''

    const segments = inputValue.split('/').filter((segment) => segment && segment !== '')

    if (segments.length > 0) {
      return segments[segments.length - 1]
    }

    return undefined
  }

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    let handle = extractHandle(inputValue)

    if (fieldName === 'tiktok_link' && handle && !handle.startsWith('@')) {
      handle = `@${handle}`
    }

    if (handle && platform) {
      field.setValue(`${platform.prefix}${handle}`)
    } else if (!inputValue) {
      field.setValue('')
    }
  }

  return (
    <div className="flex flex-col gap-y-2 w-full">
      {!!label && (
        <div className="inline-flex items-center">
          <Label htmlFor={id || fieldName}>{label}</Label>
          {labelEnd}
        </div>
      )}

      {isReadOnly ? (
        <Typography variant="label" tone={!field.state.value ? 'muted' : 'default'}>
          {field.state.value ? field.state.value : readOnlyEmptyText}
        </Typography>
      ) : (
        <div className="relative">
          <Input
            id={id || fieldName}
            type="text"
            value={field.state.value ?? ''}
            onChange={(e) => field.handleChange((e.target.value || '').toLowerCase())}
            onBlur={handleOnBlur}
            className={cn('w-full', className)}
            {...props}
            placeholder={platform?.placeholder}
          />
        </div>
      )}

      {type !== 'hidden' && <FieldInfo />}
    </div>
  )
}
